import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { QRCodeSVG } from 'qrcode.react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Slider from '@mui/material/Slider';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { minisiteGet, minisitePost, minisiteUpload, compressImage } from '../utils/minisiteApi';
import {
  MINISITE_FONTS, MINISITE_TEXT_FONT_KEYS, MINISITE_COLORS, MINISITE_ANIMATIONS,
  MINISITE_AMBIANCES, MINISITE_DISPOSITIONS, resolveMinisiteColor, minisiteFontsHref,
} from '../utils/minisiteTheme';
import { MINISITE_BLOCS, getOccasion, resolveBlocs, hasModule } from '../utils/minisiteOccasions';
import { resolveMediaUrl } from '../utils/media';
import MinisiteView from './MinisiteView';
import MinisiteRsvpResponses from './MinisiteRsvpResponses';

// Éditeur des faire-part et invitations animés, ouvert par le lien secret reçu par e-mail après
// l'achat (/personnaliser-mini-site/:slug/:token). La fiche produit ne fixe que le modèle de
// départ : tout se règle ici. La liste des champs enregistrables est bornée côté serveur
// (minisiteSanitizeParams dans apiEtsy.php) : un champ ajouté ici doit l'être aussi là-bas.
// Seuls les modules ACHETÉS sont proposés (hasModule, cf. minisiteModules.json) ; le serveur
// applique la même règle (minisiteStripModules dans apiEtsy.php).

const MAX_ETAPES = 12;
const MAX_DETAILS = 6;
const MAX_QUESTIONS = 3;
const PHOTO_FORMES = [{ key: 'rond', label: 'Ronde' }, { key: 'arche', label: 'En arche' }, { key: 'carre', label: 'Carrée' }];
const CADRAGES = [{ key: 'haut', label: 'Haut' }, { key: 'centre', label: 'Centre' }, { key: 'bas', label: 'Bas' }];

const Section = ({ title, hint, children }) => (
  <Box sx={{ marginBottom: 3 }}>
    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{title}</Typography>
    {hint && <Typography variant="body2" color="text.secondary" sx={{ marginBottom: 1 }}>{hint}</Typography>}
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: 1 }}>{children}</Box>
  </Box>
);

const Choice = ({ label, value, options, onChange }) => (
  <Box>
    <Typography variant="body2" sx={{ marginBottom: 0.5 }}>{label}</Typography>
    <ToggleButtonGroup exclusive size="small" value={value} onChange={(_, v) => v && onChange(v)}>
      {options.map((o) => <ToggleButton key={o.key} value={o.key}>{o.label}</ToggleButton>)}
    </ToggleButtonGroup>
  </Box>
);

const formatJour = (iso) => (iso
  ? new Date(`${iso}T12:00:00`).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })
  : '');

const MinisiteEditor = () => {
  const { slug, token } = useParams();
  const [form, setForm] = useState(null);
  const [savedJson, setSavedJson] = useState('');
  const [expireLe, setExpireLe] = useState('');
  const [error, setError] = useState('');
  const [tab, setTab] = useState('textes');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState('');
  const [replayKey, setReplayKey] = useState(0);
  const [responseCount, setResponseCount] = useState(null);
  const photoInput = useRef(null);
  const fondInput = useRef(null);

  useEffect(() => {
    minisiteGet('getMinisite', { slug, token })
      .then((data) => {
        if (!data?.success) return setError('Page introuvable.');
        if (!data.data.can_edit) return setError('Ce lien d\'édition n\'est pas valide. Utilisez le lien reçu par e-mail après votre commande.');
        setForm(data.data.params || {});
        setSavedJson(JSON.stringify(data.data.params || {}));
        return setExpireLe(data.data.en_ligne_jusqu_au || '');
      })
      .catch(() => setError('Impossible de charger la page.'));
  }, [slug, token]);

  const dirty = form !== null && JSON.stringify(form) !== savedJson;

  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const field = (key) => ({ value: form[key] ?? '', onChange: (e) => set(key, e.target.value), fullWidth: true });
  const toggle = (key, defaultOn = false) => ({
    checked: form[key] === undefined ? defaultOn : !!form[key],
    onChange: (e) => set(key, e.target.checked),
  });
  const handleResponseCount = useCallback((n) => setResponseCount(n), []);

  const applyServerParams = (params) => {
    setForm(params);
    setSavedJson(JSON.stringify(params));
  };

  const save = (quiet = false) => {
    setSaving(true);
    return minisitePost('updateMinisiteParams', { slug, token, params: form })
      .then((data) => {
        if (!data?.success) throw new Error(data?.message || 'Erreur lors de l\'enregistrement');
        applyServerParams(data.params);
        if (!quiet) toast.success('Enregistré !');
        return data.params;
      })
      .catch((e) => { toast.error(e.message || 'Erreur lors de l\'enregistrement'); throw e; })
      .finally(() => setSaving(false));
  };

  // Les réglages en cours sont enregistrés AVANT l'envoi : le serveur renvoie ensuite la page
  // complète, image comprise, et rien de ce qui a été saisi n'est perdu.
  const handleUpload = async (emplacement, file) => {
    if (!file) return;
    setUploading(emplacement);
    try {
      if (dirty) await save(true);
      const blob = await compressImage(file, emplacement === 'fond' ? 1800 : 1200);
      const data = await minisiteUpload({ slug, token, emplacement }, blob);
      if (!data?.success) throw new Error(data?.message || 'Envoi impossible');
      applyServerParams(data.params);
      toast.success(emplacement === 'fond' ? 'Fond enregistré !' : 'Photo enregistrée !');
    } catch (e) {
      toast.error(e.message || 'Envoi impossible');
    } finally {
      setUploading('');
    }
  };

  const handleDeleteImage = async (emplacement) => {
    try {
      if (dirty) await save(true);
      const data = await minisitePost('deleteMinisiteImage', { slug, token, emplacement });
      if (data?.success) applyServerParams(data.params);
    } catch (e) { /* message déjà affiché par save() */ }
  };

  if (error) return <Typography variant="h6" sx={{ textAlign: 'center', padding: 6 }}>{error}</Typography>;
  if (!form) return <Typography sx={{ textAlign: 'center', padding: 6 }}>Chargement…</Typography>;

  const occasion = getOccasion(form);
  const shareUrl = `${window.location.origin}/i/${slug}`;
  const accent = resolveMinisiteColor(form.couleur);
  const isCustomColor = /^#[0-9a-fA-F]{6}$/.test(form.couleur || '');
  const blocs = resolveBlocs(form);
  const has = (key) => hasModule(form, key);
  const tabs = [
    { key: 'textes', label: 'Textes' },
    { key: 'style', label: 'Style' },
    (has('photo') || has('fond_perso')) && { key: 'images', label: has('photo') && has('fond_perso') ? 'Photo et fond' : has('photo') ? 'Photo' : 'Fond' },
    has('programme') && { key: 'programme', label: 'Programme' },
    (has('lieu') || has('cagnotte')) && { key: 'lieu', label: has('lieu') && has('cagnotte') ? 'Lieu et cagnotte' : has('lieu') ? 'Lieu' : 'Cagnotte' },
    has('rsvp') && { key: 'reponses', label: responseCount !== null ? `Réponses des invités (${responseCount})` : 'Réponses des invités' },
    { key: 'partage', label: 'Sections et partage' },
  ].filter(Boolean);

  // Listes éditables (programme, informations, questions) : mêmes gestes partout.
  const listOf = (key) => (Array.isArray(form[key]) ? form[key] : []);
  const setItem = (key, index, value) => set(key, listOf(key).map((item, i) => (i === index ? value : item)));
  const removeItem = (key, index) => set(key, listOf(key).filter((_, i) => i !== index));
  const moveItem = (key, list, index, delta) => {
    const next = [...list];
    [next[index], next[index + delta]] = [next[index + delta], next[index]];
    set(key, next);
  };
  const moveButtons = (listKey, list, index) => (
    <>
      <IconButton size="small" aria-label="Monter" disabled={index === 0} onClick={() => moveItem(listKey, list, index, -1)}><ArrowUpwardIcon fontSize="small" /></IconButton>
      <IconButton size="small" aria-label="Descendre" disabled={index === list.length - 1} onClick={() => moveItem(listKey, list, index, 1)}><ArrowDownwardIcon fontSize="small" /></IconButton>
    </>
  );

  const imageSlot = (emplacement, url, inputRef, label) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
      {url && (
        <Box component="img" src={resolveMediaUrl(url)} alt="" sx={{ width: 84, height: 84, objectFit: 'cover', borderRadius: 2, border: '1px solid rgba(0,0,0,0.15)' }} />
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        hidden
        onChange={(e) => { handleUpload(emplacement, e.target.files?.[0]); e.target.value = ''; }}
      />
      <Button variant="outlined" disabled={!!uploading} onClick={() => inputRef.current?.click()}>
        {uploading === emplacement ? 'Envoi en cours…' : url ? `Remplacer ${label}` : `Choisir ${label}`}
      </Button>
      {url && <Button color="inherit" disabled={!!uploading} onClick={() => handleDeleteImage(emplacement)}>Retirer</Button>}
    </Box>
  );

  return (
    <Box sx={{ maxWidth: 1100, margin: '0 auto', padding: { xs: 1, sm: 3 } }}>
      <Helmet>
        <title>Personnalisez votre {occasion.produit.toLowerCase()} — Joli Ptit Mot</title>
        <meta name="robots" content="noindex, nofollow" />
        <link rel="stylesheet" href={minisiteFontsHref()} />
      </Helmet>
      <ToastContainer position="top-right" autoClose={3000} />
      <Typography variant="h5" gutterBottom>Personnalisez votre {occasion.produit.toLowerCase()}</Typography>

      <Grid container spacing={4}>
        <Grid item xs={12} md={4}>
          <Box sx={{ position: { md: 'sticky' }, top: { md: 16 } }}>
            <div className="ms-phone">
              <MinisiteView key={replayKey} embedded params={form} slug={slug} />
            </div>
            <Box sx={{ textAlign: 'center', marginTop: 1 }}>
              <Button size="small" onClick={() => setReplayKey((k) => k + 1)}>Rejouer l'animation</Button>
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12} md={8}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile sx={{ marginBottom: 3 }}>
            {tabs.map((t) => <Tab key={t.key} value={t.key} label={t.label} />)}
          </Tabs>

          {tab === 'textes' && (
            <>
              <Section title="L'écran d'accueil">
                <TextField label="Phrase d'introduction" placeholder={occasion.intro} inputProps={{ maxLength: 60 }} {...field('intro')} />
                <TextField label={occasion.nomsLabel} placeholder={occasion.nomsPlaceholder} inputProps={{ maxLength: 80 }} {...field('prenoms')} />
                <TextField label="Ligne sous le(s) prénom(s) (facultatif)" placeholder={occasion.sousTitrePlaceholder || 'ex. fête ses 40 ans'} inputProps={{ maxLength: 80 }} {...field('sous_titre')} />
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField label={occasion.dateLabel} type="date" InputLabelProps={{ shrink: true }} {...field('date')} />
                  <TextField label="Heure" type="time" InputLabelProps={{ shrink: true }} {...field('heure')} />
                </Box>
                {occasion.compteARebours && has('compte_a_rebours') && (
                  <FormControlLabel control={<Switch {...toggle('compte_a_rebours', true)} />} label="Afficher le compte à rebours" />
                )}
              </Section>
              {has('texte') && (
              <Section title="Votre message" hint="Le texte libre affiché sous l'écran d'accueil.">
                <TextField
                  label="Votre texte"
                  placeholder={occasion.texteExemple}
                  multiline
                  rows={6}
                  inputProps={{ maxLength: 1200 }}
                  helperText={`${(form.texte || '').length}/1200 caractères`}
                  {...field('texte')}
                />
                <TextField label="Signature" placeholder="Marie & Thomas" inputProps={{ maxLength: 80 }} {...field('signature')} />
              </Section>
              )}
              {has('details') && (
              <Section title="Informations" hint="De courtes informations mises en valeur : poids et taille du bébé, parrain et marraine, tenue souhaitée…">
                <TextField label="Titre de la section (facultatif)" inputProps={{ maxLength: 60 }} {...field('details_titre')} />
                {listOf('details').map((ligne, index) => (
                  <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <TextField label="Intitulé" placeholder="Poids" value={ligne.label || ''} onChange={(e) => setItem('details', index, { ...ligne, label: e.target.value })} inputProps={{ maxLength: 40 }} sx={{ width: 170 }} />
                    <TextField label="Valeur" placeholder="3,250 kg" value={ligne.valeur || ''} onChange={(e) => setItem('details', index, { ...ligne, valeur: e.target.value })} inputProps={{ maxLength: 80 }} fullWidth />
                    {moveButtons('details', listOf('details'), index)}
                    <IconButton size="small" aria-label="Supprimer" onClick={() => removeItem('details', index)}><DeleteOutlineIcon fontSize="small" /></IconButton>
                  </Box>
                ))}
                <Box>
                  <Button startIcon={<AddIcon />} disabled={listOf('details').length >= MAX_DETAILS} onClick={() => set('details', [...listOf('details'), { label: '', valeur: '' }])}>
                    Ajouter une information
                  </Button>
                </Box>
              </Section>
              )}
            </>
          )}

          {tab === 'style' && (
            <>
              <Section title="Police des prénoms et des titres">
                <Grid container spacing={1}>
                  {MINISITE_FONTS.map((font) => (
                    <Grid item xs={6} sm={4} key={font.key}>
                      <Paper
                        variant="outlined"
                        onClick={() => set('police', font.key)}
                        sx={{
                          cursor: 'pointer', padding: 1.5, textAlign: 'center',
                          borderColor: (form.police || 'parisienne') === font.key ? accent : undefined,
                          borderWidth: (form.police || 'parisienne') === font.key ? 2 : 1,
                        }}
                      >
                        <Box sx={{ fontFamily: font.family, fontSize: '1.5rem', lineHeight: 1.3, color: accent }}>
                          {(form.prenoms || occasion.nomsPlaceholder).slice(0, 18)}
                        </Box>
                        <Typography variant="caption" color="text.secondary">{font.label}</Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
                <TextField select label="Police du texte" {...field('police_texte')} value={form.police_texte || 'dm-sans'}>
                  {MINISITE_FONTS.filter((f) => MINISITE_TEXT_FONT_KEYS.includes(f.key)).map((f) => (
                    <MenuItem key={f.key} value={f.key} sx={{ fontFamily: f.family }}>{f.label}</MenuItem>
                  ))}
                </TextField>
                <Box>
                  <Typography variant="body2">Taille des prénoms</Typography>
                  <Slider min={60} max={160} step={5} value={Number(form.taille_prenoms) || 100} onChange={(_, v) => set('taille_prenoms', v)} valueLabelDisplay="auto" valueLabelFormat={(v) => `${v} %`} />
                </Box>
              </Section>

              <Section title="Couleur" hint="Choisissez une couleur proposée, ou la vôtre avec le sélecteur.">
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                  {MINISITE_COLORS.map((color) => (
                    <Box
                      key={color.key}
                      role="button"
                      aria-label={color.label}
                      title={color.label}
                      onClick={() => set('couleur', color.key)}
                      sx={{
                        width: 34, height: 34, borderRadius: '50%', cursor: 'pointer', backgroundColor: color.hex,
                        border: '1px solid rgba(0,0,0,0.2)',
                        outline: form.couleur === color.key ? `3px solid ${color.hex}` : 'none', outlineOffset: 2,
                      }}
                    />
                  ))}
                  <Box component="label" sx={{ display: 'flex', alignItems: 'center', gap: 1, marginLeft: 1, cursor: 'pointer' }}>
                    <input
                      type="color"
                      value={accent}
                      onChange={(e) => set('couleur', e.target.value)}
                      style={{ width: 40, height: 34, padding: 0, border: 'none', background: 'none', cursor: 'pointer' }}
                    />
                    <Typography variant="body2" sx={{ fontWeight: isCustomColor ? 600 : 400 }}>
                      {isCustomColor ? `Couleur personnalisée ${form.couleur}` : 'Autre couleur…'}
                    </Typography>
                  </Box>
                </Box>
              </Section>

              <Section title="Mise en page" hint="Selon le fond, placez le texte là où l'image est la plus dégagée.">
                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                  <Choice label="Position du texte" value={form.disposition || 'centre'} options={MINISITE_DISPOSITIONS} onChange={(v) => set('disposition', v)} />
                  <Choice label="Ambiance" value={form.ambiance || 'claire'} options={MINISITE_AMBIANCES} onChange={(v) => set('ambiance', v)} />
                </Box>
                <Box>
                  <Typography variant="body2">Voile derrière le texte (lisibilité)</Typography>
                  <Slider min={0} max={100} step={5} value={form.voile === undefined ? 70 : Number(form.voile)} onChange={(_, v) => set('voile', v)} valueLabelDisplay="auto" />
                </Box>
              </Section>

              <Section title="Animation d'arrivée des prénoms">
                <TextField
                  select
                  {...field('animation')}
                  value={form.animation || 'douce'}
                  onChange={(e) => { set('animation', e.target.value); setReplayKey((k) => k + 1); }}
                >
                  {MINISITE_ANIMATIONS.map((a) => <MenuItem key={a.key} value={a.key}>{a.label}</MenuItem>)}
                </TextField>
              </Section>
            </>
          )}

          {tab === 'images' && (
            <>
              {has('photo') && (
              <Section title="Votre photo" hint="Une photo (le bébé, vous deux, la personne fêtée…). Un nouvel envoi remplace la photo précédente.">
                {imageSlot('photo', form.photo, photoInput, 'la photo')}
                {form.photo && (
                  <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                    <Choice label="Forme" value={form.photo_forme || 'rond'} options={PHOTO_FORMES} onChange={(v) => set('photo_forme', v)} />
                    <Choice
                      label="Emplacement"
                      value={form.photo_emplacement || 'accueil'}
                      options={[{ key: 'accueil', label: 'Écran d\'accueil' }, { key: 'page', label: 'Dans la page' }]}
                      onChange={(v) => set('photo_emplacement', v)}
                    />
                  </Box>
                )}
              </Section>
              )}
              {has('fond_perso') && (
              <Section
                title="Votre propre fond"
                hint="Remplacez le fond du modèle par votre image : un stade, une plage, la maison de famille… Une image en hauteur rend mieux sur téléphone. Un nouvel envoi remplace le fond précédent."
              >
                {imageSlot('fond', form.fond_perso, fondInput, 'le fond')}
                {form.fond_perso && (
                  <>
                    <FormControlLabel control={<Switch {...toggle('fond_perso_actif', true)} />} label="Utiliser mon fond (sinon, le fond du modèle)" />
                    <Choice label="Partie de l'image à garder visible" value={form.fond_cadrage || 'centre'} options={CADRAGES} onChange={(v) => set('fond_cadrage', v)} />
                    <Typography variant="body2" color="text.secondary">
                      Si le texte se lit mal sur votre image, augmentez le voile ou passez en ambiance sombre dans l'onglet « Style ».
                    </Typography>
                  </>
                )}
                <Typography variant="caption" color="text.secondary">
                  En envoyant une image, vous confirmez avoir le droit de l'utiliser.
                </Typography>
              </Section>
              )}
            </>
          )}

          {tab === 'programme' && (
            <Section title="Le programme" hint="Facultatif : la section n'apparaît que si vous ajoutez au moins une étape.">
              <TextField label="Titre de la section" placeholder="Le programme" inputProps={{ maxLength: 60 }} {...field('programme_titre')} />
              {listOf('programme').map((etape, index) => (
                <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <TextField label="Heure" placeholder="15h00" value={etape.heure || ''} onChange={(e) => setItem('programme', index, { ...etape, heure: e.target.value })} inputProps={{ maxLength: 10 }} sx={{ width: 110 }} />
                  <TextField label="Étape" placeholder="Cérémonie" value={etape.titre || ''} onChange={(e) => setItem('programme', index, { ...etape, titre: e.target.value })} inputProps={{ maxLength: 80 }} fullWidth />
                  {moveButtons('programme', listOf('programme'), index)}
                  <IconButton size="small" aria-label="Supprimer l'étape" onClick={() => removeItem('programme', index)}><DeleteOutlineIcon fontSize="small" /></IconButton>
                </Box>
              ))}
              <Box>
                <Button startIcon={<AddIcon />} disabled={listOf('programme').length >= MAX_ETAPES} onClick={() => set('programme', [...listOf('programme'), { heure: '', titre: '' }])}>
                  Ajouter une étape
                </Button>
              </Box>
            </Section>
          )}

          {tab === 'lieu' && (
            <>
              {has('lieu') && (
              <Section title="Le lieu">
                <TextField label="Nom du lieu" placeholder="Domaine des Roses" inputProps={{ maxLength: 160 }} {...field('lieu')} />
                <TextField label="Adresse" placeholder="1200 route de Puyricard, 13100 Aix-en-Provence" inputProps={{ maxLength: 200 }} {...field('adresse')} />
                <TextField
                  label="Point Google Maps précis (facultatif)"
                  placeholder="43.5297, 5.4474   ou   https://maps.app.goo.gl/…"
                  helperText="Collez les coordonnées GPS ou le lien « Partager » de Google Maps. Sans cela, le plan est trouvé à partir du nom et de l'adresse."
                  inputProps={{ maxLength: 300 }}
                  {...field('maps')}
                />
                <FormControlLabel control={<Switch {...toggle('carte_lieu', true)} />} label="Afficher le plan dans la page" />
              </Section>
              )}
              {has('cagnotte') && (
              <Section title="Cagnotte ou liste de cadeaux" hint="Un bouton vers votre cagnotte en ligne ou votre liste (Leetchi, Lydia, liste de naissance…). Nous ne gérons aucun paiement.">
                <TextField
                  label="Lien de la cagnotte"
                  placeholder="https://…"
                  error={!!form.cagnotte_url && !/^https:\/\//.test(form.cagnotte_url)}
                  helperText={form.cagnotte_url && !/^https:\/\//.test(form.cagnotte_url) ? 'Le lien doit commencer par https://' : ' '}
                  inputProps={{ maxLength: 300 }}
                  {...field('cagnotte_url')}
                />
                <TextField label="Titre de la section" placeholder="Un petit geste" inputProps={{ maxLength: 60 }} {...field('cagnotte_titre')} />
                <TextField label="Quelques mots" placeholder="Votre présence est notre plus beau cadeau. Si vous souhaitez toutefois participer…" multiline rows={2} inputProps={{ maxLength: 300 }} {...field('cagnotte_texte')} />
              </Section>
              )}
            </>
          )}

          {tab === 'reponses' && (
            <>
              <Section title="Réponses en ligne" hint="Vos invités répondent directement sur la page ; vous retrouvez leurs réponses ici.">
                <FormControlLabel control={<Switch {...toggle('rsvp_actif')} />} label="Activer les réponses en ligne" />
                {form.rsvp_actif && (
                  <>
                    <TextField label="Titre de la section" placeholder="Confirmez votre présence" inputProps={{ maxLength: 60 }} {...field('rsvp_titre')} />
                    <TextField label="Message aux invités" placeholder="Merci de nous répondre avant le 1er mai pour que nous puissions tout organiser." multiline rows={2} inputProps={{ maxLength: 400 }} {...field('rsvp_message')} />
                    <TextField label="Date limite de réponse" type="date" InputLabelProps={{ shrink: true }} helperText="Après cette date, le formulaire se ferme automatiquement." {...field('rsvp_date_limite')} />
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>Questions posées</Typography>
                    <FormControlLabel control={<Switch {...toggle('rsvp_accompagnants')} />} label="Nombre de personnes" />
                    <FormControlLabel control={<Switch {...toggle('rsvp_enfants')} />} label="Nombre d'enfants (comptés à part)" />
                    {has('rsvp_avance') && <FormControlLabel control={<Switch {...toggle('rsvp_repas')} />} label="Choix du repas" />}
                    {has('rsvp_avance') && form.rsvp_repas && (
                      <TextField label="Repas proposés" placeholder="Viande, Poisson, Végétarien" helperText="Séparez les choix par des virgules." inputProps={{ maxLength: 300 }} {...field('rsvp_repas_options')} />
                    )}
                    <FormControlLabel control={<Switch {...toggle('rsvp_allergies')} />} label="Allergies / régime particulier" />
                    <FormControlLabel control={<Switch {...toggle('rsvp_message_invite', true)} />} label="Petit mot libre" />
                    {has('rsvp_avance') && <Typography variant="body2" sx={{ fontWeight: 600 }}>Vos propres questions</Typography>}
                    {has('rsvp_avance') && listOf('rsvp_questions').map((question, index) => (
                      <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <TextField label={`Question ${index + 1}`} placeholder="Avez-vous besoin d'un hébergement ?" value={question} onChange={(e) => setItem('rsvp_questions', index, e.target.value)} inputProps={{ maxLength: 120 }} fullWidth />
                        <IconButton size="small" aria-label="Supprimer la question" onClick={() => removeItem('rsvp_questions', index)}><DeleteOutlineIcon fontSize="small" /></IconButton>
                      </Box>
                    ))}
                    {has('rsvp_avance') && (
                    <Box>
                      <Button startIcon={<AddIcon />} disabled={listOf('rsvp_questions').length >= MAX_QUESTIONS} onClick={() => set('rsvp_questions', [...listOf('rsvp_questions'), ''])}>
                        Ajouter une question
                      </Button>
                    </Box>
                    )}
                  </>
                )}
              </Section>
              {form.rsvp_actif && (
                <Section title="Les réponses reçues">
                  <MinisiteRsvpResponses slug={slug} token={token} onCount={handleResponseCount} />
                </Section>
              )}
            </>
          )}

          {tab === 'partage' && (
            <>
              <Section title="Ordre des sections" hint="Une section n'apparaît que si elle est remplie ou activée. L'écran d'accueil reste toujours en premier.">
                {blocs.map((key, index) => (
                  <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ flex: 1 }}>{index + 1}. {MINISITE_BLOCS.find((b) => b.key === key)?.label}</Typography>
                    {moveButtons('blocs', blocs, index)}
                  </Box>
                ))}
              </Section>
              <Section title="Partager" hint="Envoyez ce lien par SMS, WhatsApp ou e-mail. Vous pouvez continuer à modifier la page après l'envoi : le lien ne change pas.">
                <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Box sx={{ padding: 1, backgroundColor: '#fff', borderRadius: 1, lineHeight: 0 }}>
                    <QRCodeSVG value={shareUrl} size={132} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 220 }}>
                    <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                      <a href={shareUrl} target="_blank" rel="noreferrer">{shareUrl}</a>
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ marginTop: 1 }}>
                      Le QR code mène à la même page : pratique à glisser dans un faire-part papier ou à afficher le jour J.
                    </Typography>
                    {expireLe && (
                      <Typography variant="body2" color="text.secondary" sx={{ marginTop: 1 }}>
                        Votre page reste en ligne jusqu'au <strong>{formatJour(expireLe)}</strong> (24 mois).
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Section>
            </>
          )}

          <Paper
            elevation={3}
            sx={{ position: 'sticky', bottom: 12, padding: 1.5, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', marginTop: 2, zIndex: 2 }}
          >
            <Button variant="contained" color="primary" onClick={() => save().catch(() => {})} disabled={saving || !dirty}>
              {saving ? 'Enregistrement…' : dirty ? 'Enregistrer' : 'Enregistré'}
            </Button>
            <Box sx={{ flex: 1, minWidth: 200 }}>
              <Typography variant="caption" color="text.secondary">Lien à partager</Typography>
              <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                <a href={shareUrl} target="_blank" rel="noreferrer">{shareUrl}</a>
              </Typography>
            </Box>
            <Button size="small" onClick={() => navigator.clipboard?.writeText(shareUrl).then(() => toast.info('Lien copié'))}>
              Copier
            </Button>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default MinisiteEditor;
